import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { NonAdminPreviewBanner } from '../../../components/NonAdminPreviewBanner';
import { useAuthContext } from '../../../context/AuthContext';
import {
  RecognitionAdminAccessProvider,
  useRecognitionAdminAccess,
} from '../../context/RecognitionAdminAccessContext';
import { forgeThemeLabel, nextForgeTheme, useForgeTheme } from '../../hooks/useForgeTheme';
import { useSidebar } from '../../hooks/useSidebar';
import {
  FORGE_DESIGN_GROUPS_DASHBOARD_PATH,
  FORGE_MASTER_LABEL_PATH,
  FORGE_PR_INFERENCE_OKRS_PATH,
  FORGE_RECOGNITION_OKRS_PATH,
  FORGE_PR_REVIEW_PATH,
  FORGE_REMOTE_DC_CONFIG_PATH,
  FORGE_REMOTE_DC_TASK_LIST_PATH,
  FORGE_REMOTE_DC_TOOL_PATH,
  FORGE_REVIEW_ISSUES_DASHBOARD_PATH,
  FORGE_STORE_DC_TASK_LIST_PATH,
  FORGE_SYSTEM_HEALTH_PATH,
  FORGE_UNRESOLVED_TASK_LIST_PATH,
} from '../../paths';
import '../../styles/forge-theme.css';
import { ForgeSidebar } from './ForgeSidebar';
import { ForgeThemeIcon } from './ForgeThemeIcon';

const MOBILE_BREAKPOINT = 760;

function useIsMobileViewport(): boolean {
  const getIsMobile = () =>
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_BREAKPOINT : false;
  const [isMobile, setIsMobile] = useState(getIsMobile);

  useEffect(() => {
    const handleResize = () => setIsMobile(getIsMobile());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
}

const FORGE_PAGE_LABELS: Record<string, string> = {
  '/forge': 'Master Label',
  [FORGE_UNRESOLVED_TASK_LIST_PATH]: 'Unresolved',
  [FORGE_DESIGN_GROUPS_DASHBOARD_PATH]: 'Design Groups',
  [FORGE_REVIEW_ISSUES_DASHBOARD_PATH]: 'Review Issues',
  [FORGE_PR_INFERENCE_OKRS_PATH]: 'PR Inference OKRs',
  [FORGE_RECOGNITION_OKRS_PATH]: 'Recognition OKRs',
  [FORGE_PR_REVIEW_PATH]: 'PR review',
  [FORGE_SYSTEM_HEALTH_PATH]: 'System Health',
  [FORGE_STORE_DC_TASK_LIST_PATH]: 'Store DC',
  [FORGE_REMOTE_DC_TASK_LIST_PATH]: 'Remote DC',
  [FORGE_MASTER_LABEL_PATH]: 'Master Label',
  [FORGE_REMOTE_DC_TOOL_PATH]: 'Remote DC',
  [FORGE_REMOTE_DC_CONFIG_PATH]: 'Remote DC Configuration',
};

// Full-width labeling workspaces: the sidebar starts hidden so the image and inspector get the
// whole viewport, and the reveal button brings it back.
const SIDEBAR_AUTO_HIDDEN_PATHS = [
  FORGE_MASTER_LABEL_PATH,
  FORGE_REMOTE_DC_TOOL_PATH,
  FORGE_PR_REVIEW_PATH,
];

function getForgePageLabel(pathname: string): string {
  const match = Object.entries(FORGE_PAGE_LABELS)
    .filter(([path]) => pathname === path || pathname.startsWith(`${path}/`))
    .sort(([left], [right]) => right.length - left.length)[0];
  return match?.[1] ?? 'Forge';
}

function getAutoHiddenSidebarPath(pathname: string): string | null {
  return (
    SIDEBAR_AUTO_HIDDEN_PATHS.find(
      (path) => pathname === path || pathname.startsWith(`${path}/`)
    ) ?? null
  );
}

const ForgeLayoutContent: React.FC = () => {
  const { hasInternalAccess } = useAuthContext();
  const { hasRecognitionAdminAccess } = useRecognitionAdminAccess();
  const { theme, toggleTheme } = useForgeTheme();
  const {
    width,
    isCollapsed,
    isHidden,
    isResizing,
    toggleCollapse,
    hideSidebar,
    showSidebar,
    onDragStart,
  } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobileViewport();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [revealedAutoHiddenPath, setRevealedAutoHiddenPath] = useState<string | null>(null);
  const pageLabel = useMemo(() => getForgePageLabel(location.pathname), [location.pathname]);
  const autoHiddenSidebarPath = useMemo(
    () => getAutoHiddenSidebarPath(location.pathname),
    [location.pathname]
  );
  const isSidebarAutoHidden =
    Boolean(autoHiddenSidebarPath) && revealedAutoHiddenPath !== autoHiddenSidebarPath;
  const nextTheme = nextForgeTheme(theme);
  const isRecognitionAdminPath = [FORGE_RECOGNITION_OKRS_PATH, FORGE_PR_REVIEW_PATH].some(
    (path) => location.pathname === path || location.pathname.startsWith(`${path}/`)
  );
  const canShowForgeNavigation = hasInternalAccess || hasRecognitionAdminAccess === true;
  const recognitionOnlyNavigation = !hasInternalAccess && hasRecognitionAdminAccess === true;

  useEffect(() => {
    setIsMobileSidebarOpen(false);
    setRevealedAutoHiddenPath(null);
  }, [location.pathname]);

  useEffect(() => {
    if (isRecognitionAdminPath && hasRecognitionAdminAccess === false) {
      navigate(FORGE_SYSTEM_HEALTH_PATH, { replace: true });
    }
  }, [hasRecognitionAdminAccess, isRecognitionAdminPath, navigate]);

  useEffect(() => {
    if (!isMobile) {
      setIsMobileSidebarOpen(false);
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsMobileSidebarOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobile]);

  const handleShowSidebar = () => {
    if (autoHiddenSidebarPath) setRevealedAutoHiddenPath(autoHiddenSidebarPath);
    showSidebar();
  };

  const desktopSidebarVisible =
    canShowForgeNavigation && !isMobile && !isHidden && !isSidebarAutoHidden;
  const sidebarOffset = desktopSidebarVisible ? (isCollapsed ? 38 : width) : 0;

  return (
    <div className="forge-root" data-forge-theme={theme}>
      <div className={`forge-layout ${isMobile ? 'is-mobile-shell' : ''}`}>
        {canShowForgeNavigation && isMobile ? (
          <div className="forge-mobile-topbar">
            <button
              type="button"
              className="forge-mobile-nav-button"
              aria-label="Open navigation"
              aria-expanded={isMobileSidebarOpen}
              onClick={() => setIsMobileSidebarOpen(true)}
            >
              <MenuOutlinedIcon />
            </button>
            <div className="forge-mobile-title">{pageLabel}</div>
            <button
              type="button"
              className="forge-mobile-nav-button"
              aria-label={`Switch to ${forgeThemeLabel(nextTheme).toLowerCase()}`}
              onClick={toggleTheme}
            >
              <ForgeThemeIcon theme={nextTheme} />
            </button>
          </div>
        ) : null}

        {desktopSidebarVisible || (canShowForgeNavigation && isMobile) ? (
          <div className={`forge-sidebar-shell ${isMobileSidebarOpen ? 'is-mobile-open' : ''}`}>
            {isMobile ? (
              <button
                type="button"
                className="forge-sidebar-backdrop"
                aria-label="Close navigation"
                data-testid="forge-mobile-sidebar-backdrop"
                onClick={() => setIsMobileSidebarOpen(false)}
              />
            ) : null}
            <ForgeSidebar
              theme={theme}
              width={width}
              isCollapsed={isCollapsed}
              isResizing={isResizing}
              isMobile={isMobile}
              toggleCollapse={toggleCollapse}
              hideSidebar={hideSidebar}
              onDragStart={onDragStart}
              toggleTheme={toggleTheme}
              currentPath={location.pathname}
              hasRecognitionAdminAccess={hasRecognitionAdminAccess === true}
              recognitionOnly={recognitionOnlyNavigation}
              onMobileNavigate={isMobile ? () => setIsMobileSidebarOpen(false) : undefined}
            />
          </div>
        ) : null}
        {canShowForgeNavigation && !isMobile && (isHidden || isSidebarAutoHidden) ? (
          <button
            type="button"
            className="forge-sidebar-reveal-button"
            aria-label="Show navigation"
            title="Show navigation"
            onClick={handleShowSidebar}
          >
            <MenuOutlinedIcon />
          </button>
        ) : null}
        <main
          className={`forge-main-content ${isResizing ? 'sidebar-resizing' : ''}`}
          style={{ marginLeft: sidebarOffset }}
        >
          <NonAdminPreviewBanner />
          {isRecognitionAdminPath && hasRecognitionAdminAccess !== true ? (
            <div className="dg-loading-state" role="status">
              Checking Recognition access...
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </div>
  );
};

export const ForgeLayout: React.FC = () => (
  <RecognitionAdminAccessProvider>
    <ForgeLayoutContent />
  </RecognitionAdminAccessProvider>
);
